// Document thresholds tab: the plant-type classification (types A to D) by maximum capacity and connection voltage, synchronous area and source clause with its text.
import { useQuery } from "@tanstack/react-query";
import { GaugeIcon } from "lucide-react";

import type { PlantTypeThreshold } from "@/api/__generated__";
import { catalogListThresholdsOptions } from "@/api/__generated__/@tanstack/react-query.gen";
import { ClauseLink } from "@/components/common/ClauseLink";
import { EmptyState } from "@/components/common/EmptyState";
import { ProblemState } from "@/components/common/ProblemState";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatNumber } from "@/lib/format";

export function formatCapacity(megawatts: number): string {
  return megawatts < 1 ? `${formatNumber(megawatts * 1000, 3)} kW` : `${formatNumber(megawatts, 3)} MW`;
}

export function formatVoltage(kilovolts: number): string {
  return `${formatNumber(kilovolts, 3)} kV`;
}

export function boundRange(
  min: number | null,
  max: number | null,
  format: (value: number) => string,
): string {
  if (min === null && max === null) {
    return "Any";
  }
  if (max === null) {
    return `≥ ${format(min ?? 0)}`;
  }
  if (min === null) {
    return `< ${format(max)}`;
  }
  if (min >= max) {
    return `≥ ${format(min)}, or < ${format(max)}`;
  }
  return `≥ ${format(min)}, < ${format(max)}`;
}

function ThresholdRow({ threshold }: { threshold: PlantTypeThreshold }) {
  return (
    <TableRow>
      <TableCell>
        <Badge variant="brand">Type {threshold.plant_type}</Badge>
      </TableCell>
      <TableCell className="whitespace-nowrap tabular-nums">
        {boundRange(threshold.min_capacity_mw, threshold.max_capacity_mw, formatCapacity)}
      </TableCell>
      <TableCell className="whitespace-nowrap tabular-nums">
        {boundRange(threshold.min_connection_voltage_kv, threshold.max_connection_voltage_kv, formatVoltage)}
      </TableCell>
      <TableCell>{threshold.synchronous_area ?? "–"}</TableCell>
      <TableCell>
        {threshold.source_clause_uid ? <ClauseLink clauseUid={threshold.source_clause_uid} /> : "–"}
      </TableCell>
      <TableCell className="max-w-[520px] text-chip text-ink-2">{threshold.source_text ?? "–"}</TableCell>
    </TableRow>
  );
}

export function ThresholdsTab({ docUid }: { docUid: string }) {
  const thresholds = useQuery(catalogListThresholdsOptions({ path: { doc_uid: docUid } }));
  if (thresholds.isError) {
    return (
      <Card>
        <ProblemState
          problem={thresholds.error}
          retry={() => {
            void thresholds.refetch();
          }}
        />
      </Card>
    );
  }
  if (!thresholds.isPending && thresholds.data.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={GaugeIcon}
          title="No plant-type thresholds"
          description="This document does not classify power generating modules into types A to D."
        />
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden">
      <Table aria-label="Plant-type thresholds">
        <TableHeader>
          <TableRow>
            <TableHead>Type</TableHead>
            <TableHead>Maximum capacity</TableHead>
            <TableHead>Connection voltage</TableHead>
            <TableHead>Synchronous area</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Source text</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {thresholds.isPending
            ? [0, 1, 2, 3].map((index) => (
                <TableRow key={index} aria-hidden>
                  {[0, 1, 2, 3, 4, 5].map((cell) => (
                    <TableCell key={cell}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : thresholds.data.map((threshold) => (
                <ThresholdRow key={threshold.threshold_uid} threshold={threshold} />
              ))}
        </TableBody>
      </Table>
    </Card>
  );
}
